import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Helmet } from "react-helmet-async";
const PowerBIServices = () => {
  const phrases = [
    "Data Analytics",
    "Power BI Services"
  ];

  const cardData = [
    { type: 'purple-clay', content: '📊' },
    { type: 'smooth-wave', content: '📈' },
    { type: 'abstract-blocks', content: '🔍' },
    { type: 'pink-sphere', content: '⚡' }
  ];

  const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0);
  const [displayedText, setDisplayedText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [cardIndex, setCardIndex] = useState(0);

  useEffect(() => {
    const currentPhrase = phrases[currentPhraseIndex];
    let timer;

    const handleTyping = () => {
      if (!isDeleting) {
        setDisplayedText(currentPhrase.substring(0, displayedText.length + 1));
        if (displayedText === currentPhrase) {
          timer = setTimeout(() => setIsDeleting(true), 1600);
          return;
        }
      } else {
        setDisplayedText(currentPhrase.substring(0, displayedText.length - 1));
        if (displayedText === '') {
          setIsDeleting(false);
          setCurrentPhraseIndex((prev) => (prev + 1) % phrases.length);
          return;
        }
      }

      const speed = isDeleting ? 30 : 80;
      timer = setTimeout(handleTyping, speed);
    };

    timer = setTimeout(handleTyping, 100);
    return () => clearTimeout(timer);
  }, [displayedText, isDeleting, currentPhraseIndex]);

  useEffect(() => {
    const cardTimer = setInterval(() => {
      setCardIndex((prev) => (prev + 1) % cardData.length);
    }, 1100);
    return () => clearInterval(cardTimer);
  }, []);

  // Intersection Observer for Scroll Animations
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.15
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-visible');
        }
      });
    }, observerOptions);

    const animatedElements = document.querySelectorAll(
      '.scroll-anim-fade-in, .scroll-anim-fade-in-up, .scroll-anim-fade-in-left, .scroll-anim-fade-in-right, .scroll-anim-flip, .scroll-anim-zoom-in'
    );

    animatedElements.forEach((el) => observer.observe(el));

    return () => {
      animatedElements.forEach((el) => observer.unobserve(el));
    };
  }, []);

  // Content Data
  const designServices = [
    { icon: "📊", title: "Custom Dashboard Architecture", desc: "Design high-impact, clean reporting canvas models focused directly on high-level operational KPIs." },
    { icon: "🔄", title: "ETL & Modern Data Prep", desc: "Clean fragmented structures and engineer reliable internal data formatting workflows using Power Query." },
    { icon: "📐", title: "Complex DAX Modeling", desc: "Draft high-performance Data Analysis Expressions to formulate dynamic time-intelligence operations." },
    { icon: "🏢", title: "Enterprise Gateway Setup", desc: "Establish secure data transmission pathways to automatically sync on-premises storage to the cloud." },
    { icon: "☁️", title: "Power BI Premium Architecture", desc: "Structure specialized governance models, workspace partition layouts, and dedicated capacity parameters." },
    { icon: "📱", title: "Mobile Layout Fine-Tuning", desc: "Re-engineer display viewports providing clean executive visualization frameworks on tablet and mobile viewports." },
    { icon: "🔐", title: "Row-Level Security (RLS)", desc: "Enforce strict security permissions ensuring consumers only access data metrics matched to their roles." }
  ];

  const devServices = [
    { icon: "🧩", title: "Custom Visual Integration", desc: "Build tailored visualization types and custom chart modules extending basic core Power BI properties." },
    { icon: "🚀", title: "Embedded Analytics Engines", desc: "Embed real-time interactive dashboards directly within public applications or internal corporate intranets." },
    { icon: "⚡", title: "Query Performance Audit", desc: "Identify data model bottlenecks, tune indexing settings, and re-architect star schema configurations." },
    { icon: "📈", title: "Paginated Corporate Reports", desc: "Construct highly formatted, print-ready page layouts using specialized Power BI Report Builder features." },
    { icon: "📡", title: "Real-Time Tracking Flows", desc: "Configure immediate dashboard update listening layers to monitor dynamic operational pipelines continuously." },
    { icon: "🔗", title: "Multi-Source Storage Bridges", desc: "Link distinct enterprise environments directly, spanning Cloud SQL solutions, ERP nodes, and CRM tables." },
    { icon: "🤖", title: "Predictive Analytics Setup", desc: "Deploy automated regression calculations, AI text analytics, and forecasting layers natively inside charts." },
    { icon: "💎", title: "Data Flow Automation", desc: "Develop reusable central data asset targets across organizational structures to establish one version of truth." },
    { icon: "🛠️", title: "Governance & Lifecycle Care", desc: "Deploy deployment pipeline steps, track system usage, and manage tenant settings smoothly." }
  ];

  const technologies = {
    "Core Platforms": ["Power BI Desktop", "Power BI Service", "Power BI Premium", "Power BI Embedded"],
    "Data Processing": ["Power Query", "M Query Language", "DAX (Data Analysis Expressions)", "SQL"],
    "Report Structuring": ["Star Schema Modeling", "Power BI Report Builder", "Tabular Editor", "DAX Studio"],
    "Data Connectivity": ["Azure SQL", "Microsoft Fabric", "SharePoint", "Dynamics 365", "SAP", "Salesforce"],
    "Administration": ["Power BI Gateways", "REST APIs", "ALM Toolkit", "Azure Active Directory"]
  };

  const processSteps = [
    { num: "01", title: "Source Assessment", desc: "Analyzing current operational files, pipeline storage nodes, and critical reporting goals." },
    { num: "02", title: "Schema Engineering", desc: "Defining clear star schema dimensional metrics, connection models, and storage techniques." },
    { num: "03", title: "Transform Operations", desc: "Applying rigorous cleaning passes, structural normalization rules, and data formatting formulas." },
    { num: "04", title: "Visual Formulation", desc: "Assembling clean interface visuals, conditional alerts, and straightforward dashboard view navigation." },
    { num: "05", title: "Security Enforcement", desc: "Testing row-level data segregation boundaries, access rights, and gateway sync configurations." },
    { num: "06", title: "Tenant Deployment", desc: "Publishing verified analytical models into target production spaces with automated update timers." },
    { num: "07", title: "Model Optimization", desc: "Monitoring refresh times, gathering continuous feedback, and tuning performance metrics." }
  ];

  const industries = [
    "Supply Chain & Logistics", "Healthcare Analytics", "Financial Planning & Analysis", "Retail & M-Commerce",
    "Manufacturing Operations", "Human Capital Management", "Banking & Risk Assessment", "Real Estate Portfolio",
    "Energy & Utilities", "Professional Consulting", "SaaS & Tech Performance", "Corporate Governance"
  ];

  const designBenefits = [
    "Unified Star Analytics", "Self-Service Business Insights", "Sub-Second Query Speeds",
    "Robust Governance Frameworks", "Role-Based Metric Paths", "Automated Data Processing",
    "Interactive Scenario Testing", "Cross-Platform Dashboard Access"
  ];

  const whyChooseUs = [
    "Certified Business Intelligence Architects", "Custom High-Performance Data Models", "Clean Executive Visual Formats",
    "Bulletproof Row-Level Protections", "Deep Multi-Source Processing Expertise", "Scalable Enterprise Capacity Setup",
    "Optimized Refresh Performance Triggers", "Clear Architecture Execution Trajectory", "On-Time Analytic Workspace Releases",
    "Continuous Workspace Operations Upkeep", "Modern Microsoft Fabric Technology Stack", "100% Data Integrity Verification Commit"
  ];

  return (
    <>

      <Helmet>
        {/* Primary SEO */}
        <title>
          Power BI Services | Business Intelligence & Data Analytics | Affsoft IT Solutions Pvt. Ltd.
        </title>

        <meta
          name="title"
          content="Power BI Services | Business Intelligence & Data Analytics | Affsoft IT Solutions Pvt. Ltd."
        />

        <meta
          name="description"
          content="Affsoft IT Solutions Pvt. Ltd. provides expert Power BI services, including dashboard development, data visualization, business intelligence solutions, data integration, custom reports, KPI dashboards, Power BI consulting, and analytics solutions to help businesses make data-driven decisions."
        />

        <meta
          name="keywords"
          content="Power BI Services, Power BI Development, Microsoft Power BI, Business Intelligence, Data Analytics, Power BI Dashboard, Data Visualization, Power BI Consulting, Custom Power BI Reports, KPI Dashboard, Business Analytics, Data Reporting, Power BI Integration, Power BI Implementation, Enterprise BI Solutions, Interactive Dashboards, Business Intelligence Company, Affsoft IT Solutions Pvt. Ltd."
        />

        <meta name="robots" content="index, follow" />
        <meta name="author" content="Affsoft IT Solutions Pvt. Ltd." />
        <meta name="language" content="English" />
        <meta name="theme-color" content="#0d5c53" />

        {/* Canonical */}
        <link
          rel="canonical"
          href="https://www.affsoft.com/power-bi-services"
        />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta
          property="og:site_name"
          content="Affsoft IT Solutions Pvt. Ltd."
        />
        <meta
          property="og:title"
          content="Professional Power BI Services | Business Intelligence Solutions | Affsoft IT Solutions Pvt. Ltd."
        />
        <meta
          property="og:description"
          content="Transform your business data into actionable insights with Power BI services from Affsoft IT Solutions Pvt. Ltd. We build interactive dashboards, custom reports, and scalable business intelligence solutions."
        />
        <meta
          property="og:url"
          content="https://www.affsoft.com/power-bi-services"
        />
        <meta
          property="og:image"
          content="https://www.affsoft.com/images/affsoft-logo.png"
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Power BI Services | Business Intelligence & Data Analytics | Affsoft IT Solutions Pvt. Ltd."
        />
        <meta
          name="twitter:description"
          content="Unlock the power of your business data with Power BI solutions from Affsoft IT Solutions Pvt. Ltd. We deliver interactive dashboards, custom reports, business intelligence, and advanced analytics."
        />
        <meta
          name="twitter:image"
          content="https://www.affsoft.com/images/affsoft-logo.png"
        />
      </Helmet>
      <div className="power-bi-services-page bg-light" style={{ fontFamily: '"Inter", sans-serif', overflowX: 'hidden' }}>

        {/* ================= HERO SECTION ================= */}
        <div
          className="hero-container container-fluid d-flex align-items-center justify-content-center position-relative overflow-hidden"
          style={{
            backgroundImage: "url('./images/home.jpg')",
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            minHeight: '85vh',
            backgroundColor: '#0a0915' // dark fallback
          }}
        >
          {/* Background Grid Lines */}
          <div className="grid-overlay position-absolute top-0 start-0 w-100 h-100" style={{ opacity: 0.15 }}>
            <div className="grid-line line-v-1"></div>
            <div className="grid-line line-v-2"></div>
            <div className="grid-line line-v-3"></div>
          </div>

          {/* Infinite Slow-Scrolling Marquee Text Background Wrapper */}
          <div className="marquee-wrapper select-none position-absolute w-100 scroll-anim-fade-in" style={{ top: '15%', opacity: 0.05 }}>

          </div>

          <div className="marquee-wrapper select-none position-absolute w-100 scroll-anim-fade-in">
            <div className="marquee-content text-white">
              <span>Data Analytics &nbsp; and &nbsp;</span>
              <span>Power BI Services </span>
            </div>
          </div>

          {/* Main Content Layout */}
          <div className="container position-relative z-3 my-5">
            <div className="row align-items-center py-4">

              {/* Left Side Info */}
              <div className="col-12 col-md-5 text-start d-flex flex-column align-items-start hero-left-pane text-white mb-4 mb-md-0 scroll-anim-fade-in-left">
                <div className="asterisk-icon mb-3 text-warning fs-3">✳</div>
                <h1 className="fw-bold mb-3 display-5">Power BI Services</h1>
                <p className="tagline mb-4 text-white" style={{ fontSize: '1rem', lineHeight: '1.6', opacity: 0.9 }}>
                  At Affsoft IT Solutions Pvt. Ltd., we convert disparate corporate data arrays into interactive, production-grade visual assets. Our advanced analytical frameworks deliver deep context mapping, reliable query processing, and automated scheduling execution to ensure data choices happen with confidence.
                </p>
                <button className="btn btn-outline-light d-flex align-items-center gap-2 px-4 py-2 rounded-pill fw-semibold">
                  <span>➔</span>
                  <span>About Affsoft</span>
                </button>
              </div>

              {/* Center Section */}
              <div className="col-12 col-md-4 text-center mb-4 mb-md-0 hero-center-pane scroll-anim-flip">
                <div className="typing-badge px-4 py-3 mx-auto bg-dark bg-opacity-75 rounded-pill border border-secondary shadow-lg d-inline-block">
                  <span className="typed-text text-white fw-mono fs-4">{displayedText}</span>
                  <span className="cursor text-warning fs-4 animate-blink">|</span>
                </div>
              </div>

              {/* Right Section */}
              <div className="col-12 col-md-3 d-flex justify-content-center justify-content-md-end hero-right-pane scroll-anim-fade-in-right">
                <div className="glass-card-frame shadow-lg p-3 bg-white bg-opacity-10 rounded-4 border border-light" style={{ width: '220px', backdropFilter: 'blur(10px)' }}>
                  <div className={`inner-graphic-card graphic-${cardData[cardIndex].type} d-flex align-items-center justify-content-center rounded-3 bg-dark`} style={{ height: '180px' }}>
                    <span className="graphic-icon display-3">{cardData[cardIndex].content}</span>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* ================= DATA ANALYSIS & SCHEMA SECTION ================= */}
        <section className="py-5 bg-white">
          <div className="container">
            <div className="text-center max-width-700 mx-auto mb-5 scroll-anim-fade-in-up">
              <span className="badge bg-primary px-3 py-2 rounded-pill mb-2 text-uppercase">Data Intelligence</span>
              <h2 className="fw-bold text-dark display-6">Clear Visual Dashboards Formulated for Strategy</h2>
              <p className="text-muted mt-3">
                Unorganized operational records limit strategic tracking capability. At Affsoft IT Solutions Pvt. Ltd., we construct secure model structures that surface key performance signals and enable immediate operational visibility.
              </p>
            </div>

            <div className="row g-4">
              {designServices.map((service, index) => (
                <div className="col-12 col-md-6 col-lg-4 scroll-anim-zoom-in" key={index} style={{ animationDelay: `${index * 0.1}s` }}>
                  <div className="card h-100 shadow-sm border-0 rounded-4 p-4 hover-translate transition-all">
                    <div className="fs-1 mb-3 text-primary">{service.icon}</div>
                    <h4 className="fw-bold fs-5 mb-2 text-dark">{service.title}</h4>
                    <p className="text-muted mb-0 small">{service.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Business Intelligence Checklist */}
            <div className="mt-5 p-4 rounded-4 bg-light border scroll-anim-fade-in-up">
              <h5 className="fw-bold text-center text-dark mb-4">Why Deploy Advanced Power BI Architectures?</h5>
              <div className="row g-3">
                {designBenefits.map((benefit, index) => (
                  <div className="col-6 col-md-3 d-flex align-items-center gap-2" key={index}>
                    <span className="text-success fs-5">✓</span>
                    <span className="text-secondary small fw-medium">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ================= ADVANCED BI OPERATIONS ================= */}
        <section className="py-5 bg-light">
          <div className="container">
            <div className="text-center max-width-700 mx-auto mb-5 scroll-anim-fade-in-up">
              <span className="badge bg-success px-3 py-2 rounded-pill mb-2 text-uppercase">Enterprise Systems</span>
              <h2 className="fw-bold text-dark display-6">Scalable Data Pipelines Configured for Precision</h2>
              <p className="text-muted mt-3">
                We leverage analytical engine structures to streamline data collection across business frameworks. From deeply embedded workspaces to optimized analysis loops, our engineering delivers stable analytics infrastructure built to scale.
              </p>
            </div>

            <div className="row g-4">
              {devServices.map((service, index) => (
                <div className="col-12 col-md-6 col-lg-4 scroll-anim-zoom-in" key={index} style={{ animationDelay: `${index * 0.1}s` }}>
                  <div className="card h-100 shadow-sm border-0 rounded-4 p-4 bg-white hover-translate transition-all">
                    <div className="fs-1 mb-3 text-success">{service.icon}</div>
                    <h4 className="fw-bold fs-5 mb-2 text-dark">{service.title}</h4>
                    <p className="text-muted mb-0 small">{service.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= TECHNOLOGIES SECTION ================= */}
        <section className="py-5 bg-dark text-white">
          <div className="container">
            <div className="text-center mb-5 scroll-anim-fade-in-up">
              <span className="badge bg-warning text-dark px-3 py-2 rounded-pill mb-2 text-uppercase fw-bold">Analytics Stack</span>
              <h2 className="fw-bold display-6">BI Ecosystem Components We Leverage</h2>
              <p className="text-light-50">We analyze and execute structural layout engineering using trusted Microsoft framework libraries.</p>
            </div>

            <div className="row g-4 justify-content-center">
              {Object.entries(technologies).map(([category, list], index) => (
                <div className="col-12 col-sm-6 col-md-4 col-lg scroll-anim-fade-in" key={index}>
                  <div className="bg-secondary bg-opacity-25 p-4 rounded-4 h-100 border border-secondary border-opacity-50">
                    <h5 className="fw-bold text-warning mb-3 border-bottom border-secondary pb-2">{category}</h5>
                    <ul className="list-unstyled mb-0">
                      {list.map((tech, i) => (
                        <li key={i} className="mb-2 d-flex align-items-center gap-2 small">
                          <span className="text-warning opacity-75">•</span> {tech}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= ROADMAP PROCESS ================= */}
        <section className="py-5 bg-white">
          <div className="container">
            <div className="text-center mb-5 scroll-anim-fade-in-up">
              <span className="badge bg-secondary px-3 py-2 rounded-pill mb-2 text-uppercase">Development Pipeline</span>
              <h2 className="fw-bold text-dark display-6">Our Agile Data Lifecycle Flow</h2>
              <p className="text-muted">A structured milestone matrix guiding information compilation into ready dashboards.</p>
            </div>

            <div className="row g-3">
              {processSteps.map((step, index) => (
                <div className="col-12 col-md-6 col-lg scroll-anim-fade-in-up" key={index} style={{ animationDelay: `${index * 0.15}s` }}>
                  <div className="p-4 rounded-4 bg-light border-start border-primary border-4 h-100 shadow-sm">
                    <div className="fw-bold text-primary display-6 mb-2">{step.num}</div>
                    <h5 className="fw-bold text-dark fs-6">{step.title}</h5>
                    <p className="text-muted small mb-0">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= INDUSTRIES SERVED ================= */}
        <section className="py-5 bg-light">
          <div className="container text-center">
            <h2 className="fw-bold text-dark mb-4 display-6 scroll-anim-fade-in-up">Industries We Empower</h2>
            <div className="d-flex flex-wrap justify-content-center gap-2 max-width-900 mx-auto scroll-anim-zoom-in">
              {industries.map((industry, index) => (
                <span className="badge bg-white text-dark border px-3 py-2 rounded-pill shadow-sm fs-7 fw-medium" key={index}>
                  🏢 {industry}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* ================= WHY CHOOSE AFFSOFT ================= */}
        <section className="py-5 bg-white border-top">
          <div className="container">
            <div className="text-center mb-5 scroll-anim-fade-in-up">
              <h2 className="fw-bold text-dark display-6">Why Partner with Affsoft IT Solutions Pvt. Ltd.?</h2>
              <p className="text-muted">We combine precise calculation setups with structural model auditing to scale data metrics safely.</p>
            </div>

            <div className="row g-3">
              {whyChooseUs.map((reason, index) => (
                <div className="col-12 col-md-6 col-lg-4 scroll-anim-fade-in-left" key={index} style={{ animationDelay: `${(index % 3) * 0.1}s` }}>
                  <div className="d-flex align-items-center gap-3 p-3 bg-light rounded-3 h-100">
                    <span className="text-primary fs-4">🛡️</span>
                    <span className="text-dark fw-semibold small">{reason}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Custom Styles Injection */}
        <style>{`
        .hover-translate {
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        .hover-translate:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 20px rgba(0,0,0,0.1) !important;
        }
        .text-light-50 {
          color: rgba(255, 255, 255, 0.7);
        }
        .max-width-700 {
          max-width: 700px;
        }
        .max-width-900 {
          max-width: 900px;
        }
        .animate-blink {
          animation: blink 1s step-end infinite;
        }
        @keyframes blink {
          from, to { color: transparent }
          50% { color: inherit }
        }

        /* --- Scroll Animation Core --- */
        .scroll-anim-fade-in,
        .scroll-anim-fade-in-up,
        .scroll-anim-fade-in-left,
        .scroll-anim-fade-in-right,
        .scroll-anim-flip,
        .scroll-anim-zoom-in {
          opacity: 0;
          will-change: transform, opacity;
          transition: all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        }

        /* Visibility Trigger via IntersectionObserver */
        .animate-visible {
          opacity: 1 !important;
          transform: none !important;
        }

        /* --- Animations Configurations --- */
        .scroll-anim-fade-in-up {
          transform: translateY(40px);
        }
        .scroll-anim-fade-in-left {
          transform: translateX(-50px);
        }
        .scroll-anim-fade-in-right {
          transform: translateX(50px);
        }
        .scroll-anim-zoom-in {
          transform: scale(0.85);
        }
        .scroll-anim-flip {
          transform: perspective(400px) rotateY(90deg);
          transition: all 1s cubic-bezier(0.175, 0.885, 0.32, 1.275);
        }
        
        .scroll-anim-flip.animate-visible {
          transform: perspective(400px) rotateY(0deg) !important;
        }
      `}</style>

      </div>

    </>
  );
};

export default PowerBIServices;